import { AssetClassCode } from '@/types/assets';
import { supabase } from '../SupabaseClient/supabase-client';
import type { AllocationData, PortfolioData } from '@/types';

const ASSET_CLASS_META: Record<AssetClassCode, { name: string; color: string }> = {
  [AssetClassCode.ACAO]: { name: 'A\u00e7\u00f5es', color: 'var(--color-brand)' },
  [AssetClassCode.FII]: { name: 'FIIs', color: 'var(--color-accent-fii)' },
  [AssetClassCode.BDRS]: { name: 'BDRs', color: 'var(--color-accent-acao)' },
};

const toNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toString = (value: unknown) => (typeof value === 'string' ? value : '');

const getAllocationCurrentValue = (value: Record<string, unknown>) =>
  toNumber(
    value.current ??
      value.currentValue ??
      value.value ??
      value.invested ??
      value.total ??
      value.total_quantity ??
      value.totalQuantity ??
      value.total_value ??
      value.totalValue,
    0,
  );

const mapAllocation = (input: unknown, totalAssets?: number): AllocationData | null => {
  if (!input || typeof input !== 'object') return null;
  const value = input as Record<string, unknown>;

  const category = value.category as AssetClassCode
  const classMeta = ASSET_CLASS_META[category];

  if (!category) return null;

  const percentageRaw = toNumber(
    value.target_percentage ??
      value.targetPercentage ??
      value.percentage ??
      value.target ??
      value.goal,
    0,
  );

  const current = getAllocationCurrentValue(value);
  const goalRaw = toNumber(value.goal ?? value.targetValue ?? value.meta, 0);
  const targetPercentage = percentageRaw > 1 ? percentageRaw / 100 : percentageRaw;
  const goalFromPercentage =
    totalAssets && targetPercentage > 0 ? totalAssets * targetPercentage : 0;
  const goal =
    goalRaw > 0
      ? goalRaw
      : goalFromPercentage > 0
        ? goalFromPercentage
        : percentageRaw > 1
          ? percentageRaw
          : Math.round(percentageRaw * 100);
  const color =
    classMeta?.color ||
    toString(value.color) ||
    toString(value.assetClassColor) ||
    toString(value.asset_class_color) ||
    'var(--color-brand)';
  const status: AllocationData['status'] = current >= goal ? 'above' : 'below';

  return {
    category,
    current,
    goal,
    color,
    status,
  };
};

export const mapPortfolioResponse = (input: unknown): PortfolioData => {
  const payload = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const allocationsSource =
    (Array.isArray(payload.allocations) && payload.allocations) ||
    (Array.isArray(payload.items) && payload.items) ||
    (Array.isArray(payload.classes) && payload.classes) ||
    [];

  const totalFromItems = allocationsSource.reduce((sum, item) => {
    if (!item || typeof item !== 'object') return sum;
    return sum + getAllocationCurrentValue(item as Record<string, unknown>);
  }, 0);

  const totalAssetsFromPayload = toNumber(
    payload.totalAssets ??
      payload.total_assets ??
      payload.total ??
      payload.totalValue ??
      payload.patrimonio ??
      payload.value,
  );
  const totalAssets = totalAssetsFromPayload || totalFromItems;

  const allocations = allocationsSource
    .map((item) => mapAllocation(item, totalAssets || undefined))
    .filter(Boolean) as AllocationData[];

  const finalTotalAssets = totalAssets || allocations.reduce((sum, item) => sum + item.current, 0);
  const needsToConsolidate =
    payload.needsToConsolidate ??
    payload.needs_to_consolidate ??
    payload.requires_consolidation ??
    payload.should_consolidate ??
    false;

  return {
    totalAssets: finalTotalAssets,
    allocations,
    needsToConsolidate: Boolean(needsToConsolidate),
  };
};

let cachedUserId: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

const getUserId = async (): Promise<string> => {
  if (cachedUserId) return cachedUserId;

  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    cachedUserId = null;
    throw new Error('Usuário não autenticado');
  }

  cachedUserId = data.user.id;
  return cachedUserId;
};

export const portfolioService = {
  async getPortfolio(): Promise<PortfolioData> {
    const { data, error } = await supabase.functions.invoke('get-portfolio-class');
    if (error) {
      throw error;
    }

    return mapPortfolioResponse(data);
  },

  async markNeedsConsolidation(): Promise<void> {
    const userId = await getUserId();
    const { error } = await supabase
      .from('portfolio')
      .update({ needs_to_consolidate: true })
      .eq('user_id', userId);

    if (error) {
      throw error;
    }
  },
};
